import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, of, Subscription, switchMap, take, tap } from 'rxjs';
import { FeedbackService } from './feedback.service';
import { SocketService } from './socket.service';
import { PracticeSettingsStore } from './practice-settings-store.service';
import { isStateModeGroup } from '../../shared/util/state-mode.util';

@Injectable({
  providedIn: 'root'
})
export class GroupService {
  private apiUrl = 'api';
  private userGroupsSubject = new BehaviorSubject<any>(null);
  userGroups$ = this.userGroupsSubject.asObservable();
  private groupUsersSubject = new BehaviorSubject<any>(null);
  groupUsers$ = this.groupUsersSubject.asObservable();
  private selectedGroupSubject = new BehaviorSubject<any>(null);
  selectedGroup$ = this.selectedGroupSubject.asObservable();

  adjustPatientValidationStatus$ = new BehaviorSubject<string>('');

  processNewSubscription = new BehaviorSubject<boolean>(false);
  updateCardDetails = new BehaviorSubject<boolean>(false);

  constructor(
    private http: HttpClient,
    private socketService: SocketService,
    private router: Router,
    private feedbackService: FeedbackService,
    private settingsStore?: PracticeSettingsStore,
  ) {
    this.setupConnectListeners();
  }

  private setupConnectListeners(): void {
    this.socketService.listen('connectTenantProvisioned', 'group-service')
      .subscribe((payload: { groupId?: string; tenantId?: string }) => {
        const current = this.selectedGroupSubject.getValue();
        // A state unit never has Connect (tasks/state-mode.md).
        if (!current || current.id !== payload.groupId || isStateModeGroup(current)) return;
        this.selectedGroupSubject.next({
          ...current,
          connectEnabled: true,
          connectTenantId: payload.tenantId,
          connectProvisionStatus: 'provisioned',
          connectProvisionError: null,
        });
        this.feedbackService.showToast('MedicAI Connect has been provisioned for this group.');
      });

    this.socketService.listen('connectProvisionFailed', 'group-service')
      .subscribe((payload: { groupId?: string; message?: string }) => {
        const current = this.selectedGroupSubject.getValue();
        if (!current || current.id !== payload.groupId) return;
        this.selectedGroupSubject.next({
          ...current,
          connectProvisionStatus: 'failed',
          connectProvisionError: payload.message || null,
        });
      });

    this.socketService.listen('connectUserProvisioned', 'group-service')
      .subscribe((payload: { userId?: string; connectUserId?: string }) => {
        if (!payload.userId) return;
        const users = this.groupUsersSubject.getValue();
        if (!Array.isArray(users)) return;
        const updated = users.map((u: any) =>
          u.userId === payload.userId || u.id === payload.userId
            ? { ...u, connectUserId: payload.connectUserId }
            : u,
        );
        this.groupUsersSubject.next(updated);
      });
  }

  get group() {
    return this.selectedGroupSubject.getValue();
  }

  /** Merges `changes` into the selected group, for a change this user just made that the server confirmed. */
  patchSelectedGroup(changes: Record<string, any>): void {
    const current = this.selectedGroupSubject.getValue();
    if (current) this.selectedGroupSubject.next({ ...current, ...changes });
  }

  get groupUsers() {
    return this.groupUsersSubject.getValue();
  }

  getGroupOwner() {
    const users = this.groupUsers;
    const group = this.group;
    if (!group || !users || users.length === 0) return null;
    const owner = users.find((user: any) => user.userId === group.ownerId);
    return owner;
  }

  getUserGroups(userId: string, userType: string): Observable<any[]> {
    const token = localStorage.getItem('token');
    return this.http.get<any[]>(`${this.apiUrl}/user-groups/${userId}`, {
      headers: { Authorization: `Bearer ${token}` }
    }).pipe(
      tap((result: any) => {
        const groups = result.groups
        this.userGroupsSubject.next(groups);
        if (groups.length > 0) {
          let selectedGroup;
          const storedId = localStorage.getItem('selectedGroupId');

          if (storedId) {
            // First, try to find the group from localStorage
            selectedGroup = groups.find((g: any) => g.id === storedId);
          }

          // If no group was found in storage (or no storedId), try to find one owned by the user
          if (!selectedGroup) {
            selectedGroup = groups.find((g: any) => g.ownerId === userId && g.subscriptionStatus !== 'CANCELLED');
          }

          // If still no group is selected, fall back to the first in the list
          if (!selectedGroup) {
            selectedGroup = groups[0];
          }

          this.setSelectedGroup(selectedGroup);
        } else {
          if (userType === 'doctor') {
            this.showSubscriptionRequired();
          } else {
            this.router.navigate(['/settings']);
          }
        }
      })
    );
  }

  private async showSubscriptionRequired() {
    const confirmed = await this.feedbackService.showConfirmation('Subscribe To MedicAI?', 'A subscription is required to create your own patient list. If you intend on joining an existing group you do not require a subscription. Would you like to subscribe and create your own patient list now?');
    if (confirmed) {
      this.router.navigate(['/settings']);
      this.processNewSubscription.next(true);
    }
  }

  /** A saved group from the server, merged over the selected one: it carries no user-specific fields such as userRole. */
  private refreshSelectedGroup(group: any): void {
    const current = this.selectedGroupSubject.getValue();
    this.setSelectedGroup(current && current.id === group?.id ? { ...current, ...group } : group);
  }

  setSelectedGroup(group: any) {
    this.selectedGroupSubject.next(group);
    if (group?.id) {
      localStorage.setItem('selectedGroupId', group.id);
      this.socketService.joinGroup(group.id);
      this.getGroupUsers(true).pipe(take(1)).subscribe();
    }
    else {
      this.socketService.disconnect();
    }
  }



  updateGroupSettings(updates: any): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.put(`${this.apiUrl}/groups/${updates.id}/settings`, updates, {
      headers: { Authorization: `Bearer ${token}` }
    }).pipe(tap((response: any) => response.success ? this.refreshSelectedGroup(response.group) : null));
  }

  /**
   * §7 rule 8 status: may this practice apply suggested ICD-10 codes without review yet?
   *
   * Read-only and explanatory. The server refuses the setting itself (409
   * ICD_AUTO_ACCEPT_NOT_PERMITTED) — this only tells the dialog what to say about why.
   */
  getIcdAutoAcceptStatus(id: string): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.get(`${this.apiUrl}/groups/${id}/icd-auto-accept-status`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  /**
   * Domains the practice could confirm as its own, derived from its contact addresses and
   * filtered of shared/public mail providers.
   *
   * Advisory only. What the server trusts when sending is the CONFIRMED `internalEmailDomains`
   * value, saved through updateGroupSettings — nothing here is acted on until an admin ticks it.
   */
  getSuggestedInternalDomains(id: string): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.get(`${this.apiUrl}/groups/${id}/suggested-internal-domains`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  updateGroupModifiers(id: string, practiceModifers: any): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.put(`${this.apiUrl}/groups/${id}/modifiers`, { practiceModifers }, {
      headers: { Authorization: `Bearer ${token}` }
    })
  }

  updateGroupTariffOverrides(id: string, tariffOverrides: any, consumableOverrides?: any): Observable<any> {
    const token = localStorage.getItem('token');
    const body: any = {};
    if (tariffOverrides !== undefined) body.tariffOverrides = tariffOverrides;
    if (consumableOverrides !== undefined) body.consumableOverrides = consumableOverrides;
    return this.http.put(`${this.apiUrl}/groups/${id}/overrides`, body, {
      headers: { Authorization: `Bearer ${token}` }
    }).pipe(tap((response: any) => response?.group ? this.refreshSelectedGroup(response.group) : null));
  }

  updateBusinessDetails(updates: any): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.post(`${this.apiUrl}/accounting/business-details`, updates, {
      headers: { Authorization: `Bearer ${token}` }
    }).pipe(tap((response: any) => response.success ? this.refreshSelectedGroup(response.group) : null));
  }

  updateYocoDetails(updates: any): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.post(`${this.apiUrl}/accounting/yoco-details`, updates, {
      headers: { Authorization: `Bearer ${token}` }
    }).pipe(tap((response: any) => response.success ? this.refreshSelectedGroup(response.group) : null));
  }

  registerYocoWebhook(groupId: string): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.post(`${this.apiUrl}/yoco/register-webhook`, { groupId }, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  /** `fromKeptCopy`: on a practice switch, the device's kept copy while it is current (PracticeSettingsStore). */
  getGroupUsers(fromKeptCopy = false): Observable<any> {
    const selectedGroup = this.group;
    if (!selectedGroup) return of();
    const token = localStorage.getItem('token');
    const request = this.http.get(`${this.apiUrl}/groups/users/${selectedGroup.id}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    const source = fromKeptCopy && this.settingsStore ? this.settingsStore.read(selectedGroup.id, 'groupUsers', () => request) : request;
    return source.pipe(tap((response: any) => this.groupUsersSubject.next(response.users)))
  }

  inviteUser(email: string, userRole: string, inviteName: string, canSignOnDoctorsBehalf: boolean, canSeeFinancials?: boolean, canDownloadRecordings?: boolean): Observable<any> {
    const selectedGroup = this.group;
    const token = localStorage.getItem('token');
    return this.http.post(`${this.apiUrl}/group-invites`, { email, userRole, groupId: selectedGroup.id, inviteName, canSignOnDoctorsBehalf, canSeeFinancials, canDownloadRecordings }, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  removeInvite(inviteId: string): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.delete(`${this.apiUrl}/group-invites/${inviteId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  removeUser(userId: string): Observable<any> {
    const selectedGroup = this.group;
    const token = localStorage.getItem('token');
    return this.http.put(`${this.apiUrl}/groups/users/${userId}`, { id: userId, active: false, groupId: selectedGroup.id }, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  updateUserRole(userId: string, userRole: string, canSignOnDoctorsBehalf: boolean, canSeeFinancials?: boolean, canDownloadRecordings?: boolean): Observable<any> {

    const selectedGroup = this.group;
    const token = localStorage.getItem('token');
    return this.http.put(`${this.apiUrl}/groups/users/${userId}`, { id: userId, userRole, groupId: selectedGroup.id, canSignOnDoctorsBehalf, canSeeFinancials, canDownloadRecordings }, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  /*  updateUserPermission(userId: string, userPermision: boolean): Observable<any> {
 
     const selectedGroup = this.group;
     const token = localStorage.getItem('token');
     return this.http.put(`${this.apiUrl}/groups/users/${userId}`, { id: userId, userPermision, groupId: selectedGroup.id }, {
       headers: { Authorization: `Bearer ${token}` }
     });
   }  */

  getGroupInvites(): Observable<any> {
    const selectedGroup = this.group;
    const token = localStorage.getItem('token');
    return this.http.get(`${this.apiUrl}/groups/invites/${selectedGroup.id}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  acceptInvite(inviteToken: string): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.post(`${this.apiUrl}/group-invites/accept`, { token: inviteToken }, { headers: { Authorization: `Bearer ${token}` } }).pipe(
      switchMap((response: any) => {
        if (response.success) {
          // Invite accepted successfully, load user groups
          return this.getUserGroups(response.userId, '');
        } else {
          // Invite acceptance failed, return an empty observable
          return of(null);
        }
      })
    );
  }

  rejectInvite(inviteToken: string): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.post(`${this.apiUrl}/group-invites/reject`, { token: inviteToken }, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  updateGroupLetterHead(formData: FormData) {
    const selectedGroup = this.group;
    const token = localStorage.getItem('token');
    return this.http.put(`${this.apiUrl}/groups/${selectedGroup.id}/letterhead`, formData, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  deleteGroupLetterHeadImage(imageType: 'header' | 'footer') {
    const selectedGroup = this.group;
    const token = localStorage.getItem('token');
    return this.http.delete(`${this.apiUrl}/groups/${selectedGroup.id}/letterhead/${imageType}`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
  }

}